import {
  type FieldInfo,
  type IMessageType,
  RepeatType,
  ScalarType,
} from '@protobuf-ts/runtime';

import * as Arm from '@viamrobotics/web-client/component/arm/v1/arm';
import * as Common from '@viamrobotics/web-client/common/v1/common';
import * as Board from '@viamrobotics/web-client/component/board/v1/board';
import * as Encoder from '@viamrobotics/web-client/component/encoder/v1/encoder';
import * as Gantry from '@viamrobotics/web-client/component/gantry/v1/gantry';
import * as Motor from '@viamrobotics/web-client/component/motor/v1/motor';
import * as Movement from '@viamrobotics/web-client/component/movementsensor/v1/movementsensor';
import * as Power from '@viamrobotics/web-client/component/powersensor/v1/powersensor';
import * as Servo from '@viamrobotics/web-client/component/servo/v1/servo';

import { ComponentSubtypes } from './components';
import { CaptureMethodNames } from './data-capture';

export type DatumType =
  | {
      type: 'unknown';
    }
  | {
      type: 'numeric';
    }
  | {
      type: 'string';
    }
  | {
      type: 'boolean';
    }
  | {
      type: 'record';
      properties: Record<string, DatumType>;
    }
  | {
      type: 'array';
      subType: DatumType;
    };

/* Convert a gRPC FieldInfo object to a DatumType. */
const fieldToDatumType = (
  field: FieldInfo,
  handledRepeat = false
): DatumType => {
  if (field.kind !== 'scalar') {
    return { type: 'unknown' };
  }

  if (!handledRepeat && field.repeat !== RepeatType.NO) {
    return {
      type: 'array',
      subType: fieldToDatumType(field, true),
    };
  }

  switch (field.T) {
    case ScalarType.BOOL: {
      return { type: 'boolean' };
    }
    case ScalarType.STRING:
    case ScalarType.BYTES: {
      return { type: 'string' };
    }
    case ScalarType.DOUBLE:
    case ScalarType.FLOAT:
    case ScalarType.FIXED32:
    case ScalarType.FIXED64:
    case ScalarType.INT32:
    case ScalarType.INT64:
    case ScalarType.SFIXED32:
    case ScalarType.SFIXED64:
    case ScalarType.SINT32:
    case ScalarType.SINT64:
    case ScalarType.UINT32:
    case ScalarType.UINT64: {
      return { type: 'numeric' };
    }
  }
};

/* Convert a gRPC MessageType object to a DatumType. */
const messageToDatumType = <T extends object>(
  gRPCType: IMessageType<T>,
  unwrapProperty?: keyof T & string
): DatumType => {
  const type: DatumType = { type: 'record', properties: {} };

  let mappedUnwrapProperty = '';
  for (const field of gRPCType.fields) {
    if (field.jsonName === unwrapProperty) {
      mappedUnwrapProperty = field.name;
    }

    switch (field.kind) {
      case 'scalar': {
        type.properties[field.name] = fieldToDatumType(field);

        break;
      }
      case 'message': {
        // eslint-disable-next-line new-cap
        type.properties[field.name] = messageToDatumType(field.T());

        break;
      }
      case 'enum': {
        // For now, we handle enums as numbers
        type.properties[field.name] = { type: 'numeric' };

        break;
      }
    }
  }

  if (mappedUnwrapProperty !== '') {
    return type.properties[mappedUnwrapProperty] ?? { type: 'unknown' };
  }

  return type;
};

const unknownType: DatumType = { type: 'unknown' };

const worldPoseDatum = {
  [CaptureMethodNames.GetWorldPose]: messageToDatumType(
    Common.GetWorldPoseResponse,
    'pose'
  ),
};

const CaptureMethodDatumTypes: Record<string, Record<string, DatumType>> = {
  [ComponentSubtypes.AUDIO_IN]: worldPoseDatum,
  [ComponentSubtypes.AUDIO_OUT]: worldPoseDatum,
  [ComponentSubtypes.ARM]: {
    [CaptureMethodNames.JointPositions]: messageToDatumType(
      Arm.GetJointPositionsResponse,
      'positions'
    ),
    [CaptureMethodNames.EndPosition]: messageToDatumType(
      Arm.GetEndPositionResponse,
      'pose'
    ),
    ...worldPoseDatum,
  },
  [ComponentSubtypes.BASE]: worldPoseDatum,
  [ComponentSubtypes.BOARD]: {
    [CaptureMethodNames.Analogs]: messageToDatumType(
      Board.ReadAnalogReaderResponse
    ),
    [CaptureMethodNames.Gpios]: messageToDatumType(Board.GetGPIOResponse),
    ...worldPoseDatum,
  },
  [ComponentSubtypes.BUTTON]: worldPoseDatum,
  [ComponentSubtypes.CAMERA]: worldPoseDatum,
  [ComponentSubtypes.ENCODER]: {
    [CaptureMethodNames.TicksCount]: messageToDatumType(
      Encoder.GetPositionResponse
    ),
    ...worldPoseDatum,
  },
  [ComponentSubtypes.GANTRY]: {
    [CaptureMethodNames.Position]: messageToDatumType(
      Gantry.GetPositionResponse
    ),
    [CaptureMethodNames.Lengths]: messageToDatumType(Gantry.GetLengthsResponse),
    ...worldPoseDatum,
  },
  [ComponentSubtypes.GENERIC]: worldPoseDatum,
  [ComponentSubtypes.GRIPPER]: worldPoseDatum,
  [ComponentSubtypes.INPUT_CONTROLLER]: worldPoseDatum,
  [ComponentSubtypes.MOTOR]: {
    [CaptureMethodNames.Position]: messageToDatumType(
      Motor.GetPositionResponse
    ),
    [CaptureMethodNames.IsPowered]: messageToDatumType(Motor.IsPoweredResponse),
    ...worldPoseDatum,
  },
  [ComponentSubtypes.MOVEMENT_SENSOR]: {
    [CaptureMethodNames.Readings]: unknownType,
    [CaptureMethodNames.AngularVelocity]: messageToDatumType(
      Movement.GetAngularVelocityResponse,
      'angularVelocity'
    ),
    [CaptureMethodNames.CompassHeading]: messageToDatumType(
      Movement.GetCompassHeadingResponse
    ),
    [CaptureMethodNames.LinearAcceleration]: messageToDatumType(
      Movement.GetLinearAccelerationResponse,
      'linearAcceleration'
    ),
    [CaptureMethodNames.LinearVelocity]: messageToDatumType(
      Movement.GetLinearVelocityResponse,
      'linearVelocity'
    ),
    [CaptureMethodNames.Orientation]: messageToDatumType(
      Movement.GetOrientationResponse,
      'orientation'
    ),
    [CaptureMethodNames.Position]: messageToDatumType(
      Movement.GetPositionResponse
    ),
    ...worldPoseDatum,
  },
  [ComponentSubtypes.POSE_TRACKER]: worldPoseDatum,
  [ComponentSubtypes.POWER_SENSOR]: {
    [CaptureMethodNames.Readings]: unknownType,
    [CaptureMethodNames.Voltage]: messageToDatumType(Power.GetVoltageResponse),
    [CaptureMethodNames.Current]: messageToDatumType(Power.GetCurrentResponse),
    [CaptureMethodNames.Power]: messageToDatumType(Power.GetPowerResponse),
    ...worldPoseDatum,
  },
  [ComponentSubtypes.SENSOR]: {
    [CaptureMethodNames.Readings]: unknownType,
    ...worldPoseDatum,
  },
  [ComponentSubtypes.SERVO]: {
    [CaptureMethodNames.Position]: messageToDatumType(
      Servo.GetPositionResponse
    ),
    ...worldPoseDatum,
  },
  [ComponentSubtypes.SWITCH]: worldPoseDatum,
};

export const getDatumTypeOfMethod = (componentType: string, method: string) => {
  return (
    CaptureMethodDatumTypes[componentType]?.[method] ?? { type: 'unknown' }
  );
};

export const getTriggerableMethodsOfComponentType = (componentType: string) => {
  return Object.keys(CaptureMethodDatumTypes[componentType] ?? {});
};
